import { useMemo, useRef, useLayoutEffect, useState } from 'react';
import { Select, Form } from 'antd';
import styled from '@emotion/styled';
import TypeDayPickerImpl from './TypeDayPickerImpl';
import TypeWeekPickerImpl from './TypeWeekPickerImpl';
import TypeYearPickerImpl from './TypeYearPickerImpl';
import { useTranslate } from '@tolgee/react';
import useSmartField from 'hooks/useSmartField';
import { useMount, useUnmount } from 'ahooks';

type MultipleTimePickerProps = {
  fieldNamePrefix: string;
};

function MultipleTimePicker(props: MultipleTimePickerProps) {
  const { fieldNamePrefix } = props;
  const { t } = useTranslate();
  const options = useMemo(() => {
    return [
      {
        label: t('multiTimeSelect.每天'),
        value: 'DAY',
      },
      {
        label: t('multiTimeSelect.每周'),
        value: 'WEEK',
      },
      {
        label: t('multiTimeSelect.每年'),
        value: 'YEAR',
      },
    ];
  }, [t]);

  const { field, helpers } = useSmartField(`${fieldNamePrefix}.repeatType`);
  const type = field.value === 'NO_REPEAT' || !field.value ? 'DAY' : field.value;
  const [refMounted, setRefMounted] = useState(false);

  const ref = useRef(null);

  useMount(() => {
    if (field.value === 'NO_REPEAT') {
      helpers.setValue('DAY');
    }
  });

  useUnmount(() => {
    helpers.setValue('NO_REPEAT');
  });

  useLayoutEffect(() => {
    setRefMounted(true);
  }, [type]);

  const select = useMemo(() => {
    if (type === 'YEAR') {
      return <TypeYearPickerImpl hookRef={ref} fieldNamePrefix={fieldNamePrefix} />;
    }
    if (type === 'WEEK') {
      return <TypeWeekPickerImpl hookRef={ref} fieldNamePrefix={fieldNamePrefix} />;
    }
    if (type === 'DAY') {
      return <TypeDayPickerImpl hookRef={ref} fieldNamePrefix={fieldNamePrefix} />;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, fieldNamePrefix, refMounted]);

  const expandWidth = useMemo(() => {
    if (type === 'DAY') {
      return '430px';
    }
    if (type === 'WEEK') {
      return '429px';
    }
    return '390px';
  }, [type]);

  const selectAreaWidth = useMemo(() => {
    if (type === 'DAY') {
      return '332px';
    }
    if (type === 'WEEK') {
      return '354px';
    }
    return 'auto';
  }, [type]);

  return (
    <Wrapper>
      <Origin>
        <TypeSelect>
          <Form.Item noStyle>
            <Select defaultValue={type} value={type} onChange={field.onChange}>
              {options.map((option) => {
                return (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        </TypeSelect>
        <Form.Item name="i-dont-care" shouldUpdate dependencies={['type']} style={{ zIndex: 2 }}>
          <SelectArea style={{ width: selectAreaWidth }}>{select}</SelectArea>
        </Form.Item>
      </Origin>
      <ExpandWrap>
        <Expand style={{ width: expandWidth }} ref={ref} />
      </ExpandWrap>
    </Wrapper>
  );
}

const Wrapper = styled.div``;

const TypeSelect = styled.div`
  min-width: 76px;
  z-index: 1;
  &:hover,
  &:active,
  &:focus {
    z-index: 4;
  }
`;

const SelectArea = styled.div`
  margin-left: -1px;
  width: 332px;
  z-index: 2;
  &:hover {
    z-index: 3;
  }
`;

const Origin = styled.div`
  display: flex;
  & > .ant-form-item {
    margin-bottom: 0;
  }
`;

const ExpandWrap = styled.div`
  background: var(--fett-erp-card-bg);
  &:not(:empty) {
    margin-top: 8px;
  }
`;

const Expand = styled.div`
  .ant-col.ant-form-item-control {
    flex-grow: 1;
    flex-shrink: unset;
    flex-basis: unset;
  }
  .ant-row.ant-form-item {
    margin-bottom: 0;
    flex-flow: column wrap;
    .ant-form-item-label {
      text-align: left;
    }
  }
`;

export default MultipleTimePicker;
